import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { NgClass } from '@angular/common';
import { distinctUntilChanged, map, merge, mergeMap, of, takeUntil, tap } from 'rxjs'
import { ResponsiveComponent } from '@nx-superprep/shared'
import { LibraryService, ModuleStats, PerformanceService, PerformanceStats, StudyService } from '@nx-superprep/backend/firebase'
import { log } from '@nx-superprep/utils'
import { LessonTermsComponent } from "../../drawer/drawer-content/lesson-terms/lesson-terms.component"
import { NavigationBarComponent, NavigationBarState } from "../../navigationbar/navigationbar.component"
import { LessonTile, LessonTileService } from '@nx-superprep/study'

@Component({
  selector: 'pop-ui-lesson-detail',
  standalone: true,
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  imports: [
    NgClass,
    LessonTermsComponent,
    NavigationBarComponent
  ]
})
export class LessonDetailComponent extends ResponsiveComponent implements OnInit, OnDestroy {

  private _id: string|undefined
  @Input()
  get moduleId() { return this._id}
  set moduleId(value) {
    this._id = value
    this.getData(this._id)
  }

  stats: PerformanceStats[] = []

  onClose(...args: unknown[]) {
    if (args) { log.debug(args) }
    this.closeChange.emit(true)
  }

  private _lesson: LessonTile|undefined
  @Input()
  get lesson() { return this._lesson }
  set lesson(value: LessonTile|undefined) {
    this._lesson = value
    this.getData(this._lesson?.id)
  }
  @Input() close = false
  @Output() closeChange = new EventEmitter<boolean>()

  private _defaultNavigation: NavigationBarState  =  { current: { route: 'status', type: 'close-end' }, title: '' }
  private _navigation: NavigationBarState = this._defaultNavigation
  @Input()
  get navigation() { return this._navigation }
  set navigation(value) {
    if (this.isWeb) {
      this._navigation = value
      this._navigation.action = value.action ?? (value.segments??[]).length > 1 ? 'close' : undefined
      this._navigation.segments = this.lesson
      ? [{path: 'home', title: 'Home' }, {path: this.lesson.id??'', title: this.lesson.title }]
      : undefined
    } else {
      this._navigation = this._defaultNavigation
    }
    if (this._navigation.action === 'close' && this.lesson) {
      this.lesson.selected = false
      this.closeChange.emit(true)
      this._navigation = this._defaultNavigation
    }
  }

  private _defaultStats: PerformanceStats[] = [
    { name: 'retention', value: 0 },
    { name: 'streak', value: 0},
    { name: 'completed', value: 0 }
  ]

  get isLibrary() { return !this.navigation.segments }

  getData(id = this._id ?? this.lesson?.id) {
    if (!id) { this.close = true }
    this.onCancel.next();
    const source = this.lesson?.id ? of(this.lesson) : this.getLesson(id)
    source
    .pipe(
      takeUntil(merge(this.onDestroy, this.onCancel)),
      tap(l => this._lesson = l),
      mergeMap(l => l ? this.getLessonStatus(l) : of(undefined))
    )
    .subscribe()
  }

  private lessonTileService = inject(LessonTileService);

  getLesson(id:string|undefined) {
    return this.libraryService.getModules(id)
    .pipe(
      takeUntil(merge(this.onDestroy, this.onCancel)),
      map((ms) => {const m = ms[0]; return m ? this.lessonTileService.createLessonTile(m, {submitted:false}) : undefined })
    )
  }
  getLessonStatus(lesson: LessonTile) {
    const id = lesson.id
    return this.performanceService.fetchModuleStats(false)
      .pipe(
        takeUntil(merge(this.onDestroy, this.onCancel)),
        map(performances => performances.find(s => s.id === id)),
        distinctUntilChanged(),
        map((value) => {
          if (!lesson || !value) { return }
          this.stats = this.createPerformanceStats(value, lesson.stats)
        })
      )
  }

  createPerformanceStats(value?: ModuleStats, stats: PerformanceStats[] = this._defaultStats): PerformanceStats[] {
    return value
    ? [
      {
        name: 'retention',
        value: (value.rightReviewCount + value.wrongReviewCount) > 0 ? 100 * value.rightReviewCount / (value.rightReviewCount + value.wrongReviewCount) : 0,
      },
      {
        name: 'streak',
        value: value.highestCorrectStreak,
      },
      {
        name: 'completed',
        value: 100 * value.progress,
      }
    ]
    : stats
  }

  override onPopstate() {
    this.onClose()
  }

  override get rootClassName() { return `${super.rootClassName}${this.isScrollable ? ' scrollable' : ''}` }

  readonly libraryService = inject(LibraryService)
  readonly performanceService = inject(PerformanceService)
  readonly studyService = inject(StudyService)

  constructor() {
    super()
    super.rootClassName = 'detail-root-class-name'
  }
}
